import React, { useState } from 'react';
import { arrayOf, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, FormattedDate } from '../../util/reactIntl';
import { markNoticesSeen, pickNotice, readSeenNoticeIds } from '../../util/assignmentNotices';
import NamedLink from '../NamedLink/NamedLink';

import css from './AssignmentEndedNotice.module.css';

/**
 * Dismissible "Gig Boost ended" notice.
 *
 * A de-assignment is announced in three places: the in-thread system message
 * (the record), an email (the reach), and this banner (the moment someone
 * opens the dashboard). It shows the single most recently ended assignment,
 * never reappears once dismissed, and states the same facts from whichever
 * side is reading — the Creator sees their payout, the Org sees commission.
 *
 * Dismissal lives in localStorage, read once through a lazy state initializer
 * so there is no effect and no flash of a notice the user already closed.
 */
const AssignmentEndedNotice = props => {
  const { assignments, viewerRole, assignmentsPageName, assignmentsPageParams, className } = props;

  const [seenIds, setSeenIds] = useState(() => readSeenNoticeIds());
  const notice = pickNotice(assignments, seenIds);

  if (!notice) return null;

  const handleDismiss = () => setSeenIds(markNoticesSeen([notice.id]));

  const endedAt = notice.updatedAt ? new Date(notice.updatedAt) : null;
  const counterpartName =
    viewerRole === 'creator'
      ? notice.organizationName || null
      : notice.creatorName || notice.creatorDisplayName || null;

  const messageId =
    viewerRole === 'creator'
      ? 'AssignmentEndedNotice.creatorMessage'
      : 'AssignmentEndedNotice.orgMessage';

  return (
    <div className={classNames(css.root, className)} role="status">
      <span className={css.text}>
        <FormattedMessage
          id={messageId}
          values={{
            counterpart: counterpartName || (
              <FormattedMessage id="AssignmentEndedNotice.counterpartFallback" />
            ),
            production: notice.showPageTitle || (
              <FormattedMessage id="AssignmentEndedNotice.productionFallback" />
            ),
            date: endedAt ? (
              <FormattedDate value={endedAt} year="numeric" month="long" day="numeric" />
            ) : (
              ''
            ),
          }}
        />
      </span>

      <NamedLink
        className={css.action}
        name={assignmentsPageName}
        params={assignmentsPageParams}
        onClick={handleDismiss}
      >
        <FormattedMessage id="AssignmentEndedNotice.viewDetails" />
      </NamedLink>

      <button type="button" className={css.dismiss} onClick={handleDismiss}>
        <FormattedMessage id="AssignmentEndedNotice.dismiss" />
      </button>
    </div>
  );
};

AssignmentEndedNotice.defaultProps = {
  assignments: [],
  assignmentsPageParams: {},
  className: null,
};

AssignmentEndedNotice.propTypes = {
  assignments: arrayOf(object),
  viewerRole: oneOf(['creator', 'org']).isRequired,
  assignmentsPageName: string.isRequired,
  assignmentsPageParams: object,
  className: string,
};

export default AssignmentEndedNotice;
